/**
 * Orientation Lock Utility
 * Forces the app to stay in portrait mode
 */

/**
 * Lock screen orientation to portrait mode
 * Works for PWA installations
 */
export async function lockOrientation() {
  // Check if Screen Orientation API is available
  if ('orientation' in screen && 'lock' in screen.orientation) {
    try {
      await screen.orientation.lock('portrait-primary');
      console.log('✅ Screen orientation locked to portrait');
      return true;
    } catch (error) {
      // Orientation lock can fail if:
      // 1. Not in fullscreen mode
      // 2. Not installed as PWA
      // 3. Browser doesn't support it
      console.warn('⚠️ Could not lock orientation:', error.message);
      return false;
    }
  } else {
    console.warn('⚠️ Screen Orientation API not supported');
    return false;
  }
}

/**
 * Unlock screen orientation
 */
export function unlockOrientation() {
  if ('orientation' in screen && 'unlock' in screen.orientation) {
    try {
      screen.orientation.unlock();
      console.log('✅ Screen orientation unlocked');
    } catch (error) {
      console.warn('⚠️ Could not unlock orientation:', error.message);
    }
  }
}

/**
 * Check if device is in portrait mode
 */
export function isPortrait() {
  return window.innerHeight > window.innerWidth;
}

/**
 * Add CSS-based orientation enforcement
 * This provides a fallback when API lock isn't available
 */
export function addOrientationStyles() {
  const styleId = 'orientation-lock-styles';

  // Remove existing style if present
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
  }

  // Add new style
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Force portrait layout even in landscape orientation */
    /* Only apply on phones (max-width: 767px), not tablets */
    @media (orientation: landscape) and (max-width: 767px) {
      body {
        /* Rotate the entire body to portrait */
        transform: rotate(-90deg);
        transform-origin: left top;
        width: 100vh;
        height: 100vw;
        overflow-x: hidden;
        position: absolute;
        top: 100%;
        left: 0;
      }
    }
  `;
  document.head.appendChild(style);
  console.log('✅ Orientation enforcement styles added (phones only)');
}

/**
 * Remove CSS-based orientation enforcement
 */
export function removeOrientationStyles() {
  const styleId = 'orientation-lock-styles';
  const existingStyle = document.getElementById(styleId);
  if (existingStyle) {
    existingStyle.remove();
    console.log('✅ Orientation enforcement styles removed');
  }
}

/**
 * Check if the device is likely a mobile/tablet (not a desktop)
 * Excludes fixed-screen smart displays (Amazon Echo Show, Fire TV with screen)
 */
function isMobileOrTablet() {
  // Skip orientation enforcement entirely on fixed-screen smart displays
  if (isFixedScreenDevice()) return false;

  // Check touch capability + screen size
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.screen.width < 1024 || window.screen.height < 1024;
  // User agent check as fallback
  const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  return (hasTouch && isSmallScreen) || mobileUA;
}

/**
 * Detect fixed-orientation smart display devices that cannot be rotated.
 * These devices should never see the rotation prompt.
 *
 * Detected devices:
 * - Amazon Echo Show (all models) — uses Silk browser
 * - Amazon Fire TV with screen
 * - Other fixed-screen Alexa devices
 *
 * @returns {boolean} True if the device has a fixed screen orientation
 */
function isFixedScreenDevice() {
  const ua = navigator.userAgent || '';

  // Amazon Silk browser (used on Echo Show, Fire tablets, Fire TV)
  // Echo Show UA contains both "Silk" and "Echo Show" or "AEOKN" (Echo Show device code)
  const isSilk = /Silk/i.test(ua);
  const isEchoShow = /Echo Show|AEOKN|AEOBC|AEOBP/i.test(ua);

  // If it's Silk browser AND identified as Echo Show, it's a fixed screen
  if (isSilk && isEchoShow) return true;

  // Also detect by screen characteristics: Silk + landscape-only fixed display
  // Echo Show 5: 960×480, Echo Show 8: 1280×800, Echo Show 10: 1280×800, Echo Show 15: 1920×1200
  if (isSilk && window.screen.height <= window.screen.width) {
    // Check if orientation API reports it cannot be changed
    if ('orientation' in screen && screen.orientation.type.includes('landscape')) {
      // Fixed landscape device — likely a smart display
      return true;
    }
  }

  return false;
}

/**
 * Show a message when device is in landscape mode
 * Only for phones/small tablets — never on desktop
 */
export function showLandscapeWarning() {
  const warningId = 'landscape-warning';

  // Remove existing warning if present
  const existingWarning = document.getElementById(warningId);
  if (existingWarning) {
    existingWarning.remove();
  }

  // Only show on mobile/tablet devices in landscape
  if (!isMobileOrTablet()) return;
  if (!isPortrait() && window.innerWidth < 1024) {
    const warning = document.createElement('div');
    warning.id = warningId;
    warning.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: white;
      color: var(--kassie-text-color, #333);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 2rem;
      text-align: center;
    `;
    warning.innerHTML = `
      <img src="/svg/components/ScreenRotate.svg" alt="Rotate device" style="width: 160px; height: 160px; margin-bottom: 1rem;" />
      <h2 style="margin: 0 0 1rem 0;">Draai jou toestel</h2>
      <p style="margin: 0;">Hierdie app werk die beste in portretmodus</p>
    `;
    document.body.appendChild(warning);
  }
}

/**
 * Hide landscape warning
 */
export function hideLandscapeWarning() {
  const warningId = 'landscape-warning';
  const existingWarning = document.getElementById(warningId);
  if (existingWarning) {
    existingWarning.remove();
  }
}

/**
 * Monitor orientation changes and show/hide warning
 */
export function monitorOrientation() {
  // Skip monitoring entirely on desktop
  if (!isMobileOrTablet()) {
    console.log('✅ Orientation monitoring skipped (desktop)');
    return;
  }

  // Initial check
  if (!isPortrait() && window.innerWidth < 1024) {
    showLandscapeWarning();
  }

  // Listen for orientation changes
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      if (!isPortrait() && window.innerWidth < 1024) {
        showLandscapeWarning();
      } else {
        hideLandscapeWarning();
      }
    }, 100);
  });

  // Also listen for resize (covers more cases)
  window.addEventListener('resize', () => {
    if (!isPortrait() && window.innerWidth < 1024) {
      showLandscapeWarning();
    } else {
      hideLandscapeWarning();
    }
  });

  console.log('✅ Orientation monitoring active (mobile/tablet)');
}

/**
 * Initialize orientation enforcement
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.useLock - Try to use Screen Orientation API lock
 * @param {boolean} options.useCSS - Use CSS rotation fallback
 * @param {boolean} options.useWarning - Show warning message in landscape
 */
export async function initOrientationEnforcement(options = {}) {
  const { useLock = true, useCSS = false, useWarning = true } = options;

  // Skip entirely on fixed-screen smart displays (Echo Show, etc.)
  if (isFixedScreenDevice()) {
    console.log('✅ Orientation enforcement skipped (fixed-screen smart display detected)');
    return;
  }

  console.log('🔒 Initializing orientation enforcement:', options);

  // Try API lock first (works best for PWA)
  if (useLock) {
    await lockOrientation();
  }

  // Add CSS rotation fallback
  if (useCSS) {
    addOrientationStyles();
  }

  // Show warning message
  if (useWarning) {
    monitorOrientation();
  }
}
